import {
  useCallback, useMemo, useState,
} from 'react';
import { updateMapSnapshotData } from '~/map/map.repository';
import { MapInfoInputField } from '~/map/mapInfoInputField.enum';
import { useMapDataUpdate } from '~/map/useMapDataUpdate';
import { type MapPrivacyLevel } from '../../types/map';
import { type ApiError } from '../../utils/api/apiError.helpers';
import { copy } from '../../utils/collections/collections';

export type MapPrivacyFieldErrors = ReadonlyMap<MapInfoInputField, ReadonlyArray<string>>;

export type MapPrivacySelectRequestParams = Readonly<{
  mapId: number | null;
  clientId: number | null;
  snapshotId?: number;
  privacyLevel: MapPrivacyLevel;
  password: string;
  password_confirmation: string;
}>;

const printUnknownFieldErrorsMessage = (apiError: ApiError) => {
  const unknownFieldErrors = Object.values(MapInfoInputField).reduce((errors, field) => {
    delete errors[field];
    return errors;
  }, { ...apiError.errors });
  if (Object.values(unknownFieldErrors).length > 0) {
    console.error('mapPrivacySelect: got ApiError with unknown fieldErrors: ', unknownFieldErrors);
  }
};

export const useUpdateMapPrivacy = () => {
  const [apiFieldErrors, setApiFieldErrors] = useState<MapPrivacyFieldErrors>(new Map());
  const [globalApiError, setGlobalApiError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const {
    isLoading: isMapDataLoading,
    updateMapData,
    clearErrors: clearMapUpdateErrors,
    fieldErrors: mapUpdateFieldErrors,
    globalError: mapUpdateGlobalError,
  } = useMapDataUpdate();

  const fieldErrors = useMemo(() => (
    copy.andAdd(apiFieldErrors, mapUpdateFieldErrors)
  ), [apiFieldErrors, mapUpdateFieldErrors]);

  const globalError = useMemo(() => (
    mapUpdateGlobalError ?? globalApiError
  ), [globalApiError, mapUpdateGlobalError]);

  const handleApiError = (apiError: ApiError) => {
    const knownFieldErrors = new Map(Object.values(MapInfoInputField)
      .filter(field => {
        return apiError.errors?.hasOwnProperty(field);
      })
      .map(field => [field, apiError.errors?.[field] ?? []] as const));
    setApiFieldErrors(knownFieldErrors);

    if (knownFieldErrors.size === 0) {
      console.error('mapPrivacySelect: got ApiError without any known fieldErrors, showing global error.');
      printUnknownFieldErrorsMessage(apiError);
      setGlobalApiError(apiError.message);
    }
    return { fieldErrors: knownFieldErrors, globalError: apiError.message };
  };

  const submitMapPrivacySelectRequest = (params: MapPrivacySelectRequestParams) => {
    if (!params.clientId || !params.mapId) {
      throw new Error('Cannot set map privacy when map with mapId or clientId is not loaded.');
    }

    return updateMapData({
      clientId: params.clientId,
      mapId: params.mapId,
      request: {
        privacy_level: {
          type: params.privacyLevel,
          password: params.password,
          password_confirmation: params.password_confirmation,
        },
      },
    })
      .then(() => {
        setGlobalApiError(null);
        setApiFieldErrors(new Map());
      });
  };

  const submitMapSnapshotPrivacySelectRequest = (params: MapPrivacySelectRequestParams) => {
    if (params.mapId === null || params.clientId === null || params.snapshotId === undefined) {
      throw new Error('Cannot set map snapshot privacy when map with mapId, map snapshotId or clientId is not loaded.');
    }
    setIsLoading(true);

    return updateMapSnapshotData(params.clientId, params.mapId, params.snapshotId, {
      privacy_level: {
        type: params.privacyLevel,
        password: params.password,
        password_confirmation: params.password_confirmation,
      },
    })
      .then(() => {
        setGlobalApiError(null);
        setApiFieldErrors(new Map());
      })
      .catch((e: ApiError) => {
        handleApiError(e);
      })
      .finally(() => setIsLoading(false));
  };

  const clearGlobalError = useCallback(() => {
    setGlobalApiError(null);
    clearMapUpdateErrors();
  }, [clearMapUpdateErrors]);

  const clearFieldError = useCallback((field: MapInfoInputField) => {
    setApiFieldErrors(prev => copy.andRemove(prev, [field]));
    clearMapUpdateErrors();
  }, [clearMapUpdateErrors]);

  return {
    fieldErrors,
    globalError,
    isLoading: isLoading || isMapDataLoading,
    updateMapPrivacy: submitMapPrivacySelectRequest,
    updateSnapshotPrivacy: submitMapSnapshotPrivacySelectRequest,
    clearGlobalError,
    clearFieldError,
  };
};
